import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { PayFromAnotherPhone } from '@/components/PayFromAnotherPhone';
import { Crown, Eye, EyeOff, Loader2, Check, Camera, X, Globe, Image as ImageIcon } from 'lucide-react';
import { useAuth, API } from '@/context/AuthContext';
import { toast } from 'sonner';

// Country codes with currency
const COUNTRIES = [
  { code: 'IN', name: 'India', dialCode: '+91', currency: 'INR', symbol: '₹' },
  { code: 'AE', name: 'UAE', dialCode: '+971', currency: 'AED', symbol: 'AED ' },
  { code: 'US', name: 'USA', dialCode: '+1', currency: 'USD', symbol: '$' },
  { code: 'GB', name: 'UK', dialCode: '+44', currency: 'GBP', symbol: '£' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', currency: 'SGD', symbol: 'S$' },
  { code: 'AU', name: 'Australia', dialCode: '+61', currency: 'AUD', symbol: 'A$' },
  { code: 'CA', name: 'Canada', dialCode: '+1', currency: 'CAD', symbol: 'C$' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', currency: 'SAR', symbol: 'SAR ' },
  { code: 'QA', name: 'Qatar', dialCode: '+974', currency: 'QAR', symbol: 'QAR ' },
  { code: 'KW', name: 'Kuwait', dialCode: '+965', currency: 'KWD', symbol: 'KWD ' },
  { code: 'OM', name: 'Oman', dialCode: '+968', currency: 'OMR', symbol: 'OMR ' },
  { code: 'BH', name: 'Bahrain', dialCode: '+973', currency: 'BHD', symbol: 'BHD ' },
];

// Currency conversion rates (approximate - should be fetched from API in production)
const CURRENCY_RATES = {
  INR: 1,
  USD: 0.012,
  AED: 0.044,
  GBP: 0.0095,
  SGD: 0.016,
  AUD: 0.018,
  CAD: 0.016,
  SAR: 0.045,
  QAR: 0.044,
  KWD: 0.0037,
  OMR: 0.0046,
  BHD: 0.0045,
};

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]); // Default India
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    dateOfBirth: '',
    password: '',
    confirmPassword: '',
    referralId: searchParams.get('ref') || searchParams.get('referral') || '',
    planId: searchParams.get('plan') || '',
    couponCode: searchParams.get('coupon') || '',
    country: 'IN'
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoBase64, setPhotoBase64] = useState(null);
  const [idProofPreview, setIdProofPreview] = useState(null);
  const [idProofBase64, setIdProofBase64] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const payOtherRef = useRef(false);
  const [otherRegId, setOtherRegId] = useState(null);
  const [paymentStep, setPaymentStep] = useState(false);
  const [couponApplied, setCouponApplied] = useState(null);
  const urlCoupon = searchParams.get('coupon');
  // Offer links from email campaigns: apply the discount automatically for the chosen plan
  useEffect(() => {
    if (!urlCoupon || !plans.length || (formData.couponCode || '').toUpperCase() !== urlCoupon.toUpperCase()) return;
    const p = plans.find((x) => x.id === formData.planId);
    if (!p) return;
    axios.post(`${API}/coupons/validate?code=${encodeURIComponent(urlCoupon)}&amount=${p.price || 0}&plan_id=${p.id}`)
      .then((r) => setCouponApplied(r.data)).catch(() => setCouponApplied(null));
  }, [plans, formData.planId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const { setAuthData } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const idProofInputRef = useRef(null);

  // Convert price to selected currency
  const convertPrice = (priceInINR) => {
    const rate = CURRENCY_RATES[selectedCountry.currency] || 1;
    const converted = Math.round(priceInINR * rate);
    return `${selectedCountry.symbol}${converted.toLocaleString()}`;
  };

  useEffect(() => {
    fetchPlans();
    fetchActiveCoupons();
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans?is_active=true&available_online=true`);
      setPlans(response.data);
      if (!formData.planId && response.data.length > 0) {
        setFormData(prev => ({ ...prev, planId: response.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const fetchActiveCoupons = async () => {
    try {
      const response = await axios.get(`${API}/coupons/active`);
      setActiveCoupons(response.data);
    } catch (error) {
      console.error('Failed to fetch coupons');
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Normalize any uploaded image to JPEG via Canvas. This solves HEIC (iPhone
  // default) and other formats that `<img>` cannot render, and also compresses
  // very large camera photos so the preview never appears blank.
  const normalizeImageToJpeg = (file, maxSize = 1200, quality = 0.9) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          try {
            let w = img.width;
            let h = img.height;
            if (w > h && w > maxSize) { h = Math.round((h * maxSize) / w); w = maxSize; }
            else if (h > maxSize) { w = Math.round((w * maxSize) / h); h = maxSize; }
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', quality));
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = () => reject(new Error('Unsupported image format. Please use JPG or PNG (iPhone HEIC not supported — change camera to Most Compatible in Settings).'));
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      toast.error('Image size must be less than 15MB');
      return;
    }

    // Read the file immediately — this guarantees the preview is set even if
    // canvas normalization later fails on quirky mobile browsers. Users always
    // get instant visual feedback.
    const reader = new FileReader();
    reader.onerror = () => toast.error('Failed to read file. Please try again.');
    reader.onloadend = () => {
      const rawDataUrl = reader.result;
      if (typeof rawDataUrl !== 'string' || !rawDataUrl.startsWith('data:')) {
        toast.error('Could not process this file. Please try a JPG or PNG.');
        return;
      }
      // Set immediately with raw bytes so the preview shows right away.
      setPhotoPreview(rawDataUrl);
      setPhotoBase64(rawDataUrl);

      // In the background, try to normalize to JPEG (handles HEIC, huge
      // camera photos, etc.). If it succeeds we upgrade the state. If it
      // fails, we keep the raw data URL — no user-visible regression.
      normalizeImageToJpeg(file, 1200, 0.9)
        .then((jpegDataUrl) => {
          setPhotoPreview(jpegDataUrl);
          setPhotoBase64(jpegDataUrl);
        })
        .catch(() => { /* keep raw preview — silent fallback */ });
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setPhotoBase64(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleIdProofChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      toast.error('File size must be less than 15MB');
      return;
    }

    // PDFs go straight through (browser handles preview via icon).
    if (file.type === 'application/pdf') {
      const reader = new FileReader();
      reader.onerror = () => toast.error('Failed to read file. Please try again.');
      reader.onloadend = () => {
        setIdProofPreview('pdf');
        setIdProofBase64(reader.result);
      };
      reader.readAsDataURL(file);
      return;
    }

    // Read the image immediately for instant preview; upgrade to JPEG later.
    const reader = new FileReader();
    reader.onerror = () => toast.error('Failed to read file. Please try again.');
    reader.onloadend = () => {
      const rawDataUrl = reader.result;
      if (typeof rawDataUrl !== 'string' || !rawDataUrl.startsWith('data:')) {
        toast.error('Could not process this file. Please try JPG, PNG or PDF.');
        return;
      }
      setIdProofPreview(rawDataUrl);
      setIdProofBase64(rawDataUrl);

      normalizeImageToJpeg(file, 1600, 0.92)
        .then((jpegDataUrl) => {
          setIdProofPreview(jpegDataUrl);
          setIdProofBase64(jpegDataUrl);
        })
        .catch(() => { /* keep raw preview — silent fallback */ });
    };
    reader.readAsDataURL(file);
  };

  const removeIdProof = () => {
    setIdProofPreview(null);
    setIdProofBase64(null);
    if (idProofInputRef.current) {
      idProofInputRef.current.value = '';
    }
  };

  const validateForm = () => {
    if (!formData.name || !formData.mobile || !formData.password) {
      toast.error('Please fill in all required fields');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }

    if (formData.mobile.length < 10) {
      toast.error('Please enter a valid mobile number');
      return false;
    }

    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }

    if (!formData.dateOfBirth) {
      toast.error('Please enter your date of birth');
      return false;
    }

    if (!formData.planId) {
      toast.error('Please select a membership plan');
      return false;
    }

    // Photo + ID proof are mandatory for KYC and physical card printing.
    if (!photoBase64) {
      toast.error('Please upload your profile photo');
      return false;
    }
    if (!idProofBase64) {
      toast.error('Please upload an ID proof (Aadhaar, PAN or Passport)');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) { payOtherRef.current = false; return; }

    setLoading(true);
    try {
      // Format mobile with country code
      const fullMobile = `${selectedCountry.dialCode}${formData.mobile.replace(/^0+/, '')}`;
      
      // Step 1: Initiate registration and get Razorpay order
      const response = await axios.post(`${API}/registration/initiate`, {
        name: formData.name,
        mobile: fullMobile,
        email: formData.email || null,
        date_of_birth: formData.dateOfBirth || null,
        password: formData.password,
        plan_id: formData.planId,
        referral_id: formData.referralId || null,
        photo_base64: photoBase64,
        id_proof_base64: payOtherRef.current ? idProofBase64 : null,
        country: selectedCountry.code,
        currency: selectedCountry.currency,
        coupon_code: couponApplied ? formData.couponCode : null,
        campaign_ref: searchParams.get('campaign') || null
      });

      const { registration_id, order_id, amount, razorpay_key, plan_name } = response.data;

      if (payOtherRef.current) {
        payOtherRef.current = false;
        setOtherRegId(registration_id);
        setLoading(false);
        return;
      }

      // Step 2: Open Razorpay payment
      const options = {
        key: razorpay_key,
        amount: amount * 100, // Razorpay expects amount in paise
        currency: 'INR',
        name: 'BITZ Club',
        description: `${plan_name} Membership`,
        order_id: order_id,
        handler: async function (razorpayResponse) {
          // Step 3: Complete registration after payment
          try {
            setPaymentStep(true);
            const completeResponse = await axios.post(`${API}/registration/complete`, null, {
              params: {
                registration_id: registration_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_signature: razorpayResponse.razorpay_signature
              }
            });

            // Login the user using AuthContext
            const { access_token, user } = completeResponse.data;
            setAuthData(access_token, user);
            
            // Also store in localStorage for persistence
            localStorage.setItem('bitz_token', access_token);
            localStorage.setItem('bitz_user', JSON.stringify(user));

            // Upload ID proof after registration
            if (idProofBase64) {
              try {
                const idBlob = await fetch(idProofBase64).then(r => r.blob());
                const idFormData = new FormData();
                idFormData.append('file', idBlob, 'id_proof.jpg');
                await axios.post(`${API}/members/${completeResponse.data.member_id}/id-proof`, idFormData, {
                  headers: { Authorization: `Bearer ${access_token}` }
                });
              } catch (idErr) {
                console.error('ID proof upload failed:', idErr);
              }
            }
            
            // Set flag to show E-Mandate popup after registration
            localStorage.setItem('showEMandateAfterRegistration', 'true');
            
            // Show success popup with app download option
            toast.success(
              <div className="text-center">
                <p className="font-bold text-lg mb-2">🎉 Welcome to BITZ Club!</p>
                <p className="mb-2">Member ID: <span className="font-mono">{completeResponse.data.member_id}</span></p>
                <p className="text-sm text-gray-300 mb-3">Set up auto-pay for hassle-free maintenance!</p>
                <div className="flex flex-col gap-2">
                  <button 
                    onClick={() => {
                      window.location.href = '/member?showEMandate=true';
                    }}
                    className="px-4 py-2 bg-[#D4AF37] text-black rounded font-semibold"
                  >
                    🔄 Setup Auto-Pay Now
                  </button>
                  <button 
                    onClick={() => window.location.href = '/member'}
                    className="px-4 py-2 bg-white/10 text-white rounded"
                  >
                    Skip for now
                  </button>
                </div>
              </div>,
              { duration: 15000 }
            );
            
            // Navigate to member dashboard with E-Mandate popup
            setTimeout(() => {
              window.location.href = '/member?showEMandate=true';
            }, 3000);
          } catch (error) {
            console.error('Registration completion failed:', error);
            toast.error(error.response?.data?.detail || 'Failed to complete registration');
            setPaymentStep(false);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.mobile
        },
        // UPI Intent apps + QR (UPI Collect was retired by NPCI in Feb 2026).
        config: {
          display: {
            blocks: {
              upi_block: {
                name: 'Pay via UPI',
                instruments: [
                  { method: 'upi', flows: ['intent', 'qr'] }
                ],
              },
            },
            sequence: ['block.upi_block'],
            preferences: { show_default_blocks: true },
          },
        },
        method: { upi: true, card: true, netbanking: true, wallet: true },
        theme: {
          color: '#D4AF37'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            toast.info('Payment cancelled. You can try again.');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Registration initiation failed:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.error?.description || 'Registration failed. Please try again.';
      
      // Handle Razorpay rate limiting
      if (errorMessage.toLowerCase().includes('too many requests') || 
          error.response?.data?.error?.code === 'BAD_REQUEST_ERROR') {
        toast.error(
          <div>
            <p className="font-bold">⚠️ Too Many Attempts</p>
            <p className="text-sm mt-1">Please wait 2-3 minutes before trying again.</p>
            <p className="text-xs text-gray-400 mt-2">This is a payment gateway security measure.</p>
          </div>,
          { duration: 10000 }
        );
      }
      // Check if user is already registered
      else if (errorMessage.toLowerCase().includes('already registered') || 
          errorMessage.toLowerCase().includes('already exists') ||
          errorMessage.toLowerCase().includes('mobile number already')) {
        toast.error(
          <div>
            <p>{errorMessage}</p>
            <button 
              onClick={() => window.location.href = '/login'}
              className="mt-2 px-4 py-1 bg-[#D4AF37] text-black rounded text-sm font-semibold"
            >
              Go to Login
            </button>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      if (!paymentStep) {
        setLoading(false);
      }
    }
  };

  const selectedPlan = plans.find(p => p.id === formData.planId);

  if (paymentStep) {
    return (
      <div className="min-h-screen bg-[#0F0F10] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Creating Your Membership...</h2>
          <p className="text-gray-400">Please wait while we activate your account</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F10] py-20 px-4">
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1756981168649-0e3c3c8a32f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjbHViJTIwaW50ZXJpb3IlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzczMDgwNzc5fDA&ixlib=rb-4.1.0&q=85)'
        }}
      />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-4xl mx-auto"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-2 mb-6">
            <img src="/brand/bci-logo-512.png" alt="BCI" className="w-20 h-20 object-contain drop-shadow-[0_8px_24px_rgba(212,175,55,0.4)]" />
            <div className="flex flex-col items-center leading-tight">
              <span className="text-2xl font-bold text-white tracking-wide" style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}>
                BITZ Club International
              </span>
              <span className="text-[10px] text-[#D4AF37] tracking-[0.4em] uppercase mt-1">
                Premium Worldwide Privileges
              </span>
            </div>
          </Link>
          <h1 
            className="text-3xl font-bold text-white mb-2"
            style={{ fontFamily: 'Cinzel, Playfair Display, serif', letterSpacing: '0.05em' }}
          >
            Join the <span className="text-[#D4AF37]">BCI Elite</span>
          </h1>
          <p className="text-gray-400">Create your membership account</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Registration Form */}
          <div className="glass-gold p-8">
            <h2 className="text-xl font-semibold text-white mb-1">Your details</h2>
            <p className="text-xs text-gray-400 mb-6">Takes under a minute. Photo & ID can be added later.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Photo Upload — OPTIONAL at registration; final card uses this once added */}
              <div>
                <label className="input-label">Profile photo <span className="text-xs text-red-400 font-normal normal-case ml-1">* required</span></label>
                <div className="flex items-center gap-4">
                  <div 
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-[#D4AF37]/50 flex items-center justify-center overflow-hidden bg-[#1A1A1C] cursor-pointer hover:border-[#D4AF37] transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-8 h-8 text-[#D4AF37]/50" />
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoChange}
                    accept="image/*"
                    capture="user"
                    className="hidden"
                    data-testid="register-photo"
                  />
                  <input
                    type="file"
                    ref={galleryInputRef}
                    onChange={handlePhotoChange}
                    accept="image/*"
                    className="hidden"
                    data-testid="register-photo-gallery"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-400">Adds your photo to the membership card.</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center gap-1" data-testid="register-photo-camera-btn">
                        <Camera className="w-3 h-3" /> Take Selfie
                      </button>
                      <button type="button" onClick={() => galleryInputRef.current?.click()} className="text-xs px-3 py-1.5 rounded-full border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center gap-1" data-testid="register-photo-gallery-btn">
                        <ImageIcon className="w-3 h-3" /> Choose from Gallery
                      </button>
                    </div>
                    {photoPreview && (
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="text-sm text-red-400 hover:text-red-300 mt-1 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* ID Proof Upload — OPTIONAL at registration; verification happens later */}
              <div>
                <label className="input-label">ID proof — Aadhaar, PAN or Passport <span className="text-xs text-red-400 font-normal normal-case ml-1">* required</span></label>
                <div className="flex items-center gap-4">
                  <div 
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-[#D4AF37]/50 flex items-center justify-center overflow-hidden bg-[#1A1A1C] cursor-pointer hover:border-[#D4AF37] transition-colors"
                    onClick={() => idProofInputRef.current?.click()}
                    data-testid="register-idproof-area"
                  >
                    {idProofPreview && idProofPreview !== 'pdf' ? (
                      <img src={idProofPreview} alt="ID Proof" className="w-full h-full object-cover" />
                    ) : idProofPreview === 'pdf' ? (
                      <div className="text-center p-1">
                        <svg className="w-6 h-6 text-[#D4AF37] mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                        <p className="text-[10px] text-[#D4AF37] mt-1">PDF</p>
                      </div>
                    ) : (
                      <svg className="w-8 h-8 text-[#D4AF37]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15A2.25 2.25 0 002.25 6.75v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" /></svg>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={idProofInputRef}
                    onChange={handleIdProofChange}
                    accept="image/*,.pdf"
                    className="hidden"
                    data-testid="register-idproof-input"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-400">Upload Aadhaar, PAN or Passport.</p>
                    {idProofPreview && (
                      <button
                        type="button"
                        onClick={removeIdProof}
                        className="text-sm text-red-400 hover:text-red-300 mt-1 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="input-label">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="input-gold"
                  data-testid="register-name"
                />
              </div>

              {/* Country Selection */}
              <div>
                <label className="input-label flex items-center gap-2">
                  <Globe className="w-4 h-4 text-[#D4AF37]" />
                  Country / Region *
                </label>
                <select
                  value={selectedCountry.code}
                  onChange={(e) => {
                    const country = COUNTRIES.find(c => c.code === e.target.value);
                    setSelectedCountry(country);
                    setFormData({ ...formData, country: e.target.value });
                  }}
                  className="input-gold"
                  data-testid="register-country"
                >
                  {COUNTRIES.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name} ({country.dialCode}) - {country.currency}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="input-label">Mobile Number *</label>
                <div className="flex gap-2">
                  <div className="w-24 flex-shrink-0">
                    <div className="input-gold bg-[#D4AF37]/10 text-center font-semibold text-[#D4AF37]">
                      {selectedCountry.dialCode}
                    </div>
                  </div>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                    className="input-gold flex-1"
                    maxLength={15}
                    data-testid="register-mobile"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedCountry.code === 'IN' ? 'Enter 10-digit number without country code' : 'Enter number without country code'}
                </p>
              </div>

              <div>
                <label className="input-label">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className="input-gold"
                  required
                  data-testid="register-email"
                />
              </div>

              <div>
                <label className="input-label">Date of Birth <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className="input-gold"
                  max={new Date().toISOString().split('T')[0]}
                  required
                  data-testid="register-dob"
                />
                <p className="text-xs text-gray-500 mt-1">We'll send you special birthday offers!</p>
              </div>

              <div>
                <label className="input-label">Referral / Employee Code (Optional)</label>
                <input
                  type="text"
                  name="referralId"
                  value={formData.referralId}
                  onChange={handleChange}
                  placeholder="e.g., EMP0001, DSA0001, EXE001-DSA0001"
                  className="input-gold"
                  data-testid="register-referral-id"
                />
                <p className="text-xs text-gray-500 mt-1">Employee Code, DSA Code, or Executive Code</p>
              </div>

              <div>
                <label className="input-label">Coupon Code (Optional)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="couponCode"
                    value={formData.couponCode}
                    onChange={(e) => {
                      handleChange(e);
                      setCouponApplied(null);
                    }}
                    placeholder="Enter coupon code"
                    className="input-gold flex-1"
                    data-testid="register-coupon-code"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!formData.couponCode) return;
                      setValidatingCoupon(true);
                      try {
                        const selectedPlan = plans.find(p => p.id === formData.planId || p.plan_id === formData.planId);
                        const amount = selectedPlan?.price || 0;
                        const response = await axios.post(`${API}/coupons/validate?code=${formData.couponCode}&amount=${amount}&plan_id=${formData.planId || ''}`);
                        setCouponApplied(response.data);
                        toast.success(`Coupon applied! You save ₹${response.data.discount_amount}`);
                      } catch (error) {
                        setCouponApplied(null);
                        toast.error(error.response?.data?.detail || 'Invalid coupon code');
                      } finally {
                        setValidatingCoupon(false);
                      }
                    }}
                    disabled={validatingCoupon || !formData.couponCode}
                    className="px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/30 disabled:opacity-50"
                  >
                    {validatingCoupon ? 'Checking...' : 'Apply'}
                  </button>
                </div>
                {couponApplied && (
                  <p className="text-xs text-green-400 mt-1">
                    ✓ Coupon applied! Discount: ₹{couponApplied.discount_amount}
                  </p>
                )}
              </div>

              <div>
                <label className="input-label">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a password"
                    className="input-gold pr-10"
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="input-label">Confirm Password *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  className="input-gold"
                  data-testid="register-confirm-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 mt-6"
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : couponApplied ? (
                  <>
                    <span className="line-through text-gray-400 mr-2">{convertPrice(selectedPlan?.price || 0)}</span>
                    Pay {convertPrice(couponApplied.final_amount)} & Register
                  </>
                ) : (
                  <>
                    Pay {convertPrice(selectedPlan?.price || 0)} & Register
                  </>
                )}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={(e) => { payOtherRef.current = true; e.currentTarget.form.requestSubmit(); }}
                className="w-full mt-3 py-2.5 rounded-lg border border-[#D4AF37]/50 text-[#D4AF37] text-sm hover:bg-[#D4AF37]/10 transition-colors"
                data-testid="register-pay-other-phone"
              >
                Pay from another phone (QR / link)
              </button>
            </form>
            {otherRegId && <PayFromAnotherPhone registrationId={otherRegId} onClose={() => setOtherRegId(null)} />}

            <div className="mt-6 text-center">
              <p className="text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="text-[#D4AF37] hover:underline">
                  Sign In
                </Link>
              </p>
            </div>
          </div>

          {/* Plan Selection */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white mb-4">Select Your Plan *</h2>
            
            {plans.length === 0 ? (
              <div className="card-dark p-6 text-center">
                <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin mx-auto mb-2" />
                <p className="text-gray-400">Loading plans...</p>
              </div>
            ) : (
              plans.map((plan) => (
                <div
                  key={plan.id}
                  onClick={() => setFormData(prev => ({ ...prev, planId: plan.id }))}
                  className={`p-4 cursor-pointer transition-all rounded-lg ${
                    formData.planId === plan.id
                      ? 'glass-gold gold-glow'
                      : 'card-dark card-interactive'
                  }`}
                  data-testid={`select-plan-${plan.name.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                      <p className="text-sm text-gray-400">
                        {plan.display_duration_format === 'years' 
                          ? `${Math.round((plan.duration_months || 12) / 12)} Year${Math.round((plan.duration_months || 12) / 12) > 1 ? 's' : ''}`
                          : `${plan.duration_months || plan.duration || 12} months`
                        }
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-[#D4AF37]">{convertPrice(plan.price)}</span>
                      {selectedCountry.code !== 'IN' && (
                        <p className="text-xs text-gray-500">₹{plan.price.toLocaleString()} INR</p>
                      )}
                    </div>
                  </div>
                  {/* Plan Features */}
                  {plan.features && plan.features.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
                      {plan.features
                        .filter(f => !f.toLowerCase().includes('maintenance'))
                        .map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Plan Description (if no features) */}
                  {(!plan.features || plan.features.length === 0) && plan.description && (
                    <p className="mt-2 text-xs text-gray-400">{plan.description}</p>
                  )}
                  {formData.planId === plan.id && (
                    <div className="mt-2 pt-2 border-t border-white/10">
                      <p className="text-xs text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
                        <Check className="w-4 h-4" /> Selected Plan
                      </p>
                    </div>
                  )}
                  {/* Available Coupons for this plan */}
                  {activeCoupons.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/10">
                      {activeCoupons
                        .filter(c => {
                          // Show coupon if: no plan restriction OR this plan is in the list
                          if (!c.applicable_plans || c.applicable_plans.length === 0) return true;
                          return c.applicable_plans.includes(plan.id);
                        })
                        .filter(c => !c.min_amount || plan.price >= c.min_amount)
                        .slice(0, 2)
                        .map((coupon, ci) => (
                        <button
                          key={ci}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData(prev => ({ ...prev, planId: plan.id, couponCode: coupon.code }));
                            setCouponApplied(null);
                            // Auto-apply the coupon
                            const discount = coupon.discount_type === 'percentage' 
                              ? plan.price * (coupon.discount_value / 100) 
                              : coupon.discount_value;
                            const finalDiscount = Math.min(discount, plan.price);
                            setCouponApplied({
                              code: coupon.code,
                              discount_type: coupon.discount_type,
                              discount_value: coupon.discount_value,
                              discount_amount: Math.round(finalDiscount),
                              final_amount: plan.price - Math.round(finalDiscount)
                            });
                            toast.success(`Coupon ${coupon.code} applied! You save ₹${Math.round(finalDiscount)}`);
                          }}
                          className="w-full mt-1.5 px-3 py-1.5 bg-green-900/30 border border-dashed border-green-500/40 rounded text-left hover:bg-green-900/50 transition-colors"
                          data-testid={`coupon-${coupon.code}-plan-${plan.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-green-400 font-mono font-bold">{coupon.code}</span>
                            <span className="text-xs text-green-300">
                              {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% OFF` : `₹${coupon.discount_value} OFF`}
                            </span>
                          </div>
                          <p className="text-[10px] text-green-500/70 mt-0.5">Tap to apply</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Payment Info */}
            <div className="p-4 bg-[#1A1A1C] border border-[#D4AF37]/30 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-sm text-white font-medium">Secure Payment via Razorpay</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Your membership will be activated immediately after successful payment. 
                    You'll receive your Member ID and digital card instantly.
                  </p>
                </div>
              </div>
            </div>

            {/* Resort Collection - States */}
            <div className="p-4 bg-[#1a365d] rounded-lg">
              <h3 className="text-white font-semibold mb-3 text-center">Resort Collection</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-gray-200">Karnataka</span>
                <span className="text-gray-200">Tamil Nadu</span>
                <span className="text-gray-200">Kerala</span>
                <span className="text-gray-200">Andhra Pradesh</span>
                <span className="text-gray-200">Telangana</span>
                <span className="text-gray-200">Maharashtra</span>
                <span className="text-gray-200">Goa</span>
                <span className="text-gray-200">Rajasthan</span>
                <span className="text-gray-200">Delhi NCR</span>
                <span className="text-gray-200">Uttarakhand</span>
                <span className="text-gray-200">Himachal Pradesh</span>
                <span className="text-gray-200">Punjab</span>
                <span className="text-gray-200">Gujarat</span>
                <span className="text-gray-200">West Bengal</span>
                <span className="text-gray-200">Odisha</span>
                <span className="text-gray-200">Bihar</span>
                <span className="text-gray-200">Madhya Pradesh</span>
                <span className="text-gray-200">Puducherry</span>
                <span className="text-gray-200">Uttar Pradesh</span>
                <span className="text-gray-200">And More...</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
